import { buildActionLink } from '@/utils/format.ts'

/**
 * design.json の店舗アクション設定
 */
export interface PointAction {
  id?: string
  label?: string
  class?: string
  icon?: string
  href?: {
    format?: string
  }
  target?: string
  need?: {
    key?: string
    url_params?: Record<string, string>[]
  }
  onclick?: string
  isHide?: () => boolean
}

/**
 * 組織別に登録された店舗アクション
 */
export type OrganizationActions = Record<string, ((point: Point) => void) | undefined>

/**
 * 店舗データとURLパラメータがアクションの表示条件を満たすか判定する
 *
 * @param action 店舗アクション設定
 * @param point 表示対象の店舗
 * @param currentParams 現在のURLパラメータ
 * @returns 表示条件をすべて満たす場合はtrue
 */
export function isShowAction(
  action: PointAction,
  point: Point,
  currentParams: Record<string, string>,
): boolean {
  if (!action.need) {
    return true
  }

  const needKey = action.need.key
  if (needKey) {
    const pointField = (point as Point & Record<string, unknown>)[needKey]
    if (!pointField && !point.extra_fields[needKey]) {
      return false
    }
  }

  const requiredParams = action.need.url_params
  if (requiredParams && !checkMatchUrlParams(requiredParams, currentParams)) {
    return false
  }

  return true
}

/**
 * アクションをリンクまたはボタンとして描画できるか判定する
 *
 * @param action 店舗アクション設定
 * @param organizationActions 組織別に登録された店舗アクション
 * @returns labelとhrefまたは登録済みonclickを持つ場合はtrue
 */
export function canRenderAction(
  action: PointAction,
  organizationActions: OrganizationActions,
): boolean {
  if (action.isHide && !action.isHide()) {
    return false
  }
  if (!action.label) {
    return false
  }
  if (action.href) {
    return true
  }
  return Boolean(action.onclick && organizationActions[action.onclick])
}

/**
 * メッセージ設定を反映したアクションラベルを返す
 *
 * @param action 店舗アクション設定
 * @param messages メッセージ設定
 * @returns メッセージ設定に存在すれば翻訳値、なければ設定値
 */
export function getActionLabel(
  action: PointAction,
  messages: Record<string, string>,
): string {
  if (!action.label) {
    return ''
  }
  return messages[action.label] || action.label
}

/**
 * アクション設定と店舗データからリンクURLを生成する
 *
 * @param action 店舗アクション設定
 * @param point 表示対象の店舗
 * @returns 生成したリンクURL
 */
export function getActionHref(action: PointAction, point: Point): string {
  return buildActionLink(point, action.href?.format || '')
}

/**
 * 必須URLパラメータがすべて現在値と一致するか判定する
 *
 * @param requiredParams 必須URLパラメータ
 * @param currentParams 現在のURLパラメータ
 * @returns 必須パラメータがすべて存在して値が一致する場合はtrue
 */
function checkMatchUrlParams(
  requiredParams: Record<string, string>[],
  currentParams: Record<string, string>,
): boolean {
  for (const param of requiredParams) {
    for (const key in param) {
      if (!Object.prototype.hasOwnProperty.call(currentParams, key)) {
        return false
      }
      if (currentParams[key] != param[key]) {
        return false
      }
    }
  }

  return true
}
